import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Check, Info, LogOut, Users } from "lucide-react";
import { useState } from "react";

import { useSession } from "@/auth/SessionContext";
import {
  readThemePreference,
  setThemePreference,
  type ThemePreference,
} from "@/lib/theme";
import { DeploymentIdentityDialog } from "@/ui/support/DeploymentIdentityDialog";

function initials(displayName: string): string {
  const parts = displayName.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? "?";
  const second = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
  return `${first}${second}`.toUpperCase();
}

export function UserMenu() {
  const { session, adapter } = useSession();
  const { user } = session;
  /**
   * Controlled so the deployment-details dialog below can be opened after the
   * menu has closed (the two focus moves would otherwise race).
   *
   * There is no publishing-account section in this menu any more. Publication
   * runs as the environment's GitHub App installation: the signed-in user's
   * session and Editor role are the whole precondition, so nothing here reads
   * a GitHub connection, connects one, or reconnects one.
   */
  const [open, setOpen] = useState(false);
  /**
   * #490's support surface, reachable from the account menu because that is
   * where a person already goes to ask about their own session, and because
   * "which build am I looking at" is a question every role has — it is
   * deliberately not gated on a capability. The dialog is rendered as a SIBLING
   * of the menu rather than inside it: a Radix menu unmounts its content when it
   * closes, and a dialog mounted in that tree would be torn down by the very
   * click that opened it.
   */
  const [deploymentOpen, setDeploymentOpen] = useState(false);
  const [theme, setTheme] = useState(readThemePreference);

  return (
    <>
    <DropdownMenu.Root open={open} onOpenChange={setOpen}>
      <DropdownMenu.Trigger className="flex items-center gap-3 rounded-md px-2 py-1 text-left hover:bg-secondary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring">
        <span className="hidden text-right sm:block">
          <span className="block text-sm font-semibold leading-tight text-foreground">
            {user.displayName}
          </span>
        </span>
        <span
          aria-hidden="true"
          className="flex h-9 w-9 items-center justify-center rounded-full bg-accent text-sm font-semibold text-accent-foreground"
        >
          {initials(user.displayName)}
        </span>
        <span className="sr-only">Account menu for {user.displayName}</span>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={6}
          // `min-w-56` sets a floor, not a ceiling, and the explanatory
          // paragraphs below are the widest content — so without a ceiling the
          // COPY decides the menu width. The amended-AC5 sentence measures
          // 786px, which is 411px past a 375px viewport, and the menu is
          // positioned rather than in flow, so the page does not scroll to
          // reveal what overflows: the text is simply unreachable on a phone.
          // The cap makes the paragraph wrap instead of widening the menu.
          className="z-50 min-w-56 max-w-[calc(100vw-2rem)] rounded-md border border-border bg-popover p-1 shadow-md"
        >
          <div className="px-3 py-2">
            <p className="text-sm font-medium text-foreground">
              {user.displayName}
            </p>
            <p className="text-xs text-muted-foreground">
              Signed in with{" "}
              {adapter.kind === "dev-stub"
                ? "the local development identity"
                : "Mentaily single sign-on"}
            </p>
          </div>
          <DropdownMenu.Separator className="my-1 h-px bg-border" />
          <DropdownMenu.Item
            disabled={!adapter.supportsAccountSwitch}
            // The adapter forces the provider's account picker; navigating or
            // signing out here first would either race the redirect or let the
            // provider's SSO session sign the same account straight back in.
            //
            // Amended #44: this reauthenticates through Mentaily Entra and is
            // the ONLY thing it does. It offers no local persona, no customer,
            // no clinical tenant and no environment as an account choice —
            // which is why it delegates to the adapter rather than rendering a
            // list of identities the browser could pick from.
            onSelect={() => {
              void adapter.switchAccount();
            }}
            className="flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground outline-none data-[highlighted]:bg-secondary data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50"
          >
            <Users aria-hidden="true" className="h-4 w-4" />
            Switch account
          </DropdownMenu.Item>
          <DropdownMenu.Item
            disabled={!adapter.supportsSignOut}
            // The adapter owns where sign-out lands: an OIDC sign-out has to
            // reach the provider's end-session endpoint, and navigating here
            // would race that redirect and cancel it.
            onSelect={() => {
              void adapter.signOut();
            }}
            className="flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground outline-none data-[highlighted]:bg-secondary data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50"
          >
            <LogOut aria-hidden="true" className="h-4 w-4" />
            Sign out
          </DropdownMenu.Item>
          {adapter.supportsSignOut ? (
            // Amended #44: the Editor session is Mentaily Entra's and nothing
            // else's. Publishing needs no sign-in of its own since the GitHub
            // App identity — the service publishes as the environment's App,
            // attributing the request to this signed-in user — so there is no
            // second session to mention, and none to leave live on sign-out.
            <p className="px-3 pb-2 pt-1 text-xs text-muted-foreground">
              Switching or signing out goes through Mentaily Entra. Publishing
              uses this sign-in and your Editor role; there is no separate
              GitHub sign-in to end.
            </p>
          ) : (
            <p className="px-3 pb-2 pt-1 text-xs text-muted-foreground">
              The local development identity has no session to end or switch.
            </p>
          )}

          <DropdownMenu.Separator className="my-1 h-px bg-border" />
          <DropdownMenu.Label className="px-3 pt-1 text-xs text-muted-foreground">
            Theme
          </DropdownMenu.Label>
          <DropdownMenu.RadioGroup
            value={theme}
            onValueChange={(value) => {
              const preference = value as ThemePreference;
              setThemePreference(preference);
              setTheme(preference);
            }}
          >
            {(["system", "light", "dark"] as const).map((value) => (
              <DropdownMenu.RadioItem
                key={value}
                value={value}
                onSelect={(event) => event.preventDefault()}
                className="flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm capitalize text-foreground outline-none data-[highlighted]:bg-secondary"
              >
                <span className="h-4 w-4">
                  <DropdownMenu.ItemIndicator>
                    <Check aria-hidden="true" className="h-4 w-4" />
                  </DropdownMenu.ItemIndicator>
                </span>
                {value}
              </DropdownMenu.RadioItem>
            ))}
          </DropdownMenu.RadioGroup>

          <DropdownMenu.Separator className="my-1 h-px bg-border" />
          <DropdownMenu.Item
            onSelect={() => {
              // Deferred past this event loop turn: Radix closes the menu on
              // select and restores focus to the trigger, and opening the
              // dialog synchronously makes the two focus moves race.
              setTimeout(() => setDeploymentOpen(true), 0);
            }}
            className="flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground outline-none data-[highlighted]:bg-secondary"
          >
            <Info aria-hidden="true" className="h-4 w-4" />
            Deployment details
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
    <DeploymentIdentityDialog
      open={deploymentOpen}
      onOpenChange={setDeploymentOpen}
    />
    </>
  );
}
